import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Card,
    Typography,
    Input,
    Button,
    Form,
    Spin,
    App,
    Row,
    Col,
    Select
} from 'antd';
import {v4 as uuidv4} from 'uuid';
import {authService, paymentMethodService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

const {Title, Text} = Typography;

const PaymentMethodEdit = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);
    const [formData, setFormData] = useState({
        type: [],
        pos_type: []
    });
    const [selectedType, setSelectedType] = useState(null);

    // Состояние для отслеживания предыдущего тела запроса
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        fetchData();
        setPageTitle('Редактирование способа оплаты');

        setRightContent(
            authService.hasPermission('pos:payment_methods:edit') && (<Button
                type="primary"
                onClick={handleSave}
                loading={saving}
            >
                Сохранить
            </Button>)
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
            setPaymentMethod(null);
        };
    }, [setPageTitle, setRightContent, id]);

    useEffect(() => {
        if (paymentMethod) {
            form.setFieldsValue(paymentMethod);
            setSelectedType(paymentMethod.type);
        }
    }, [paymentMethod, form]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Получаем данные способа оплаты
            const paymentMethodResponse = await paymentMethodService.getPaymentMethodById(id);

            // Получаем данные для формы
            const formDataResponse = await paymentMethodService.getFormData();

            if (paymentMethodResponse.status === 'success' && formDataResponse.status === 'success') {
                setPaymentMethod(paymentMethodResponse.data);
                setFormData(formDataResponse.data);
                console.log('Получены данные способа оплаты:', paymentMethodResponse.data);
            } else {
                message.error('Не удалось загрузить данные способа оплаты');
                navigate('/payment-methods');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Способ оплаты не найден",
                    description: "Указанный способ оплаты не существует или был удален."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных способа оплаты. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    // Обработчик изменения типа способа оплаты
    const handleTypeChange = (value) => {
        setSelectedType(value);
        form.setFieldValue('type', value);

        // Если тип не card, очищаем поля POS
        if (value !== 'card') {
            form.setFieldsValue({
                pos_type: undefined,
                pos_ip_address: undefined
            });
        }
    };
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Получаем данные пользователя из localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const username = userData.username || '';
            const firstName = userData.first_name || '';
            const lastName = userData.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values,
                username: username,
                full_name: fullName
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await paymentMethodService.savePaymentMethod(
                id,
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Способ оплаты успешно сохранен');
                fetchData(); // Обновляем данные после сохранения
            } else {
                message.error(response.message || 'Ошибка при сохранении способа оплаты');
            }
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            message.error(error.message || 'Ошибка при сохранении способа оплаты');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Способ оплаты «{paymentMethod.name}»
            </Title>
            <div style={{marginBottom: 24}}>
                <Text>ID: <Text strong>{paymentMethod.id}</Text></Text>
            </div>

            <Form
                form={form}
                layout="horizontal"
                initialValues={paymentMethod}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Основные атрибуты" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Название"
                                name="name"
                                rules={[
                                    {required: true, message: 'Введите название способа оплаты'},
                                    {max: 255, message: 'Максимальная длина - 255 символов'}
                                ]}
                            >
                                <Input placeholder="Введите название способа оплаты" maxLength={255}/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Внешний ID"
                                name="external_id"
                                rules={[
                                    {required: true, message: 'Введите внешний ID'}
                                ]}
                            >
                                <Input placeholder="Введите внешний ID"/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Тип"
                                name="type"
                                rules={[
                                    {required: true, message: 'Выберите тип способа оплаты'}
                                ]}
                            >
                                <Select
                                    options={formData.type.map(type => ({
                                        label: type.label,
                                        value: type.value
                                    }))}
                                    placeholder="Выберите тип способа оплаты"
                                    showSearch
                                    onChange={handleTypeChange}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Тип POS"
                                name="pos_type"
                            >
                                <Select
                                    options={formData.pos_type.map(posType => ({
                                        label: posType.label,
                                        value: posType.value
                                    }))}
                                    placeholder="Выберите тип POS"
                                    showSearch
                                    disabled={selectedType !== 'card'}
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="IP-адрес POS"
                                name="pos_ip_address"
                                rules={[
                                    {
                                        pattern: /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
                                        message: 'Введите корректный IP-адрес'
                                    }
                                ]}
                            >
                                <Input
                                    placeholder="Введите IP-адрес POS"
                                    disabled={selectedType !== 'card'}
                                />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default PaymentMethodEdit;